<template>
    <div class="openedByPerson">
        <div class="initialsContainer">
            <div class="initials">{{ openedBy.initials }}</div>
        </div>
        <div class="tooltip-arrow"></div>
        <div class="tooltip">
            <span class="personInfo">Geopend door <b>{{ openedBy.name }}</b>.</span>
            <span class="lastActiveInfo"><i>Laatste activiteit: {{ friendlyLastActivityText }}.</i></span>
            <span><i>Periode: {{ friendlyPeriodText }}.</i></span>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {RosterOpenedBy} from '@cquential/api';
    import Dates from '@cquential/date-utils';

    @Component
    export default class RosterOpenedByPerson extends Vue {
        @Prop({required: true}) public openedBy!: RosterOpenedBy;
        @Prop({type: Number, required: true}) public updateSecondsAgo!: number;

        public get friendlyLastActivityText(): string {
            if ((this.openedBy.lastActivitySecondsAgo + this.updateSecondsAgo) < 120) {
                return 'zojuist';
            }

            return Math.floor((this.openedBy.lastActivitySecondsAgo + this.updateSecondsAgo) / 60) + ' minuten geleden';
        }

        public get friendlyPeriodText(): string {
            return this.formatDate(this.openedBy.firstVisibleDay) + ' - ' + this.formatDate(this.openedBy.lastVisibleDay);
        }

        private formatDate(date: Date): string {
            if (date.getFullYear() !== (new Date()).getFullYear()) {
                return Dates.Formatter.formatDateWithWeekDayShortMonthAndYear(date);
            }

            return Dates.Formatter.formatDateWithWeekDayShortMonth(date);
        }
    }
</script>

<style lang="scss" scoped>
    .openedByPerson {
        // variables
        --arrow-size: 1rem; // don't use more than 4.125rem (50px)
        --margin: 0.0625rem;
        --border-width: 0.1875rem;
        --move-tooltip-right: 1.25rem;
        --tooltip-width: 19.375rem;
        --half-ball-width: calc(0.5 * var(--filterrow-height) - var(--border-width) - var(--margin)); // computed

        // sizing
        box-sizing: border-box;
        width: calc(var(--filterrow-height) - var(--margin) * 2);
        height: calc(var(--filterrow-height) - var(--margin) * 2);
        margin: var(--margin);
        display: inline-block;
        vertical-align: top;

        // purely visual stuff
        border: var(--border-width) solid var(--roster-opened-by-border-color);
        background: var(--roster-opened-by-background);
        box-shadow: var(--generic-shadow-subtle);
        border-radius: 100%;

        .initialsContainer {
            // sizing and positioning
            width: 100%;
            height: 100%;
            position: relative;
            text-align: center;
            white-space: nowrap;

            .initials {
                // sizing and positioning
                display: inline-block;
                position: relative;
                top: 50%;
                transform: translateY(-50%);
                width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        .tooltip {
            // sizing and positioning
            position: absolute;
            right: var(--move-tooltip-right);
            width: var(--tooltip-width);
            z-index: 10000;
            display: none; // shown on hover
            margin-top: calc(-0.3 * var(--arrow-size)); // position it against the arrow without using 'top' (position is absolute) (could also be done using a transform)

            // visual stuff
            border: 1px solid var(--modal-border-color);
            background: var(--modal-background);
            border-radius: 0.125rem;
            padding: 5px;

            > span {
                display: block;
            }
        }

        .tooltip-arrow {
            // this is pretty much duplicated from .keyboard-deployment-autocomplete .arrow [but it's definitely different]
            display: none;
            border-left: 1px solid var(--modal-border-color);
            border-top: 1px solid var(--modal-border-color);
            position: relative;
            left: var(--half-ball-width); // half the width of the 'ball'
            width: var(--arrow-size);
            height: var(--arrow-size);
            z-index: 10001;
            transform-origin: top left; // don't move it when rotating, i.e. the top left (the point of the arrow) stays in the same position
            transform: rotate(45deg);
            background: linear-gradient(135deg, var(--modal-background) 0%, var(--modal-background) 54%, transparent 54%);
        }

        &:nth-last-child(n + 4) {
            // dirty dirty hack: if the ball is far enough from the side of the screen, we don't position the tooltip based on the side of the screen, but we just center it below the ball
            // got a better idea? DO TELL
            .tooltip {
                position: relative;
                left: calc(-0.5 * var(--tooltip-width) + var(--half-ball-width)); // half the width of the thing itself + half the width of the 'ball'
            }
        }

        &:hover {
            .tooltip, .tooltip-arrow {
                display: block;
            }
        }
    }
</style>
